/**
 * The identity half of the legacy user API.
 *
 * Everything here that used to post to pacmny-be's `/sessionless/*` family now
 * calls the REST API or is gone. The bridge adapter deliberately intercepts
 * every `/sessionless/` URL so those calls cannot reach the retired backend, and
 * its operation table maps none of them — so *every* account-creation and
 * uniqueness probe threw `BridgeUnmappedOperation`.
 *
 * The line the port draws, and the reason the functions below split the way they
 * do: **credentials belong to pacmny-auth, people belong to pacmny-api.**
 *
 *  - Who exists, and whether an identifier is taken, is the API's business:
 *    `GET /users/availability` and `POST /users`.
 *  - Choosing or resetting a password is the authorization server's, and this
 *    SPA never handles one. `register`'s legacy twin took a password in the
 *    body and forwarded it to an unauthenticated pacmny-be action, which is an
 *    account-takeover primitive with an admin UI on top; `StoreUserRequest`
 *    marks `password` *prohibited* rather than merely unlisted so that porting
 *    the old form tells you why, once.
 *
 * Consequently `finalizeRegistration`, `verify`, `requestNewToken`,
 * `forgotPassword`, `resetPassword` and `updatePassword` are **removed**, not
 * re-pointed. The first three had no live caller left; the rest were called only
 * by the four public `Verify*Page` components, which now hand off to pacmny-auth
 * instead of setting a password themselves.
 */

import { request } from './request';
import { getRequestConfig } from './config';
import {
  UPDATE_PREFERENCES_ACTION,
  GET_PREFERENCES_ACTION,
  GET_LICENSES_ACTION,
  UPDATE_LICENSE_STATUS_ACTION,
  UPDATE_PROFILE_ACTION,
  CREATE_DEVICE,
  GET_NOTES,
  UPDATE_NOTES,
  DELETE_NOTES,
  CHANGE_PASSWORD_ACTION
} from './ajaxActions';
import {
  OK
} from './ajaxActionResponse';

import store from '@/store';
import { checkAvailability, createUser, updateUserCredentials } from '@/api2/users';
import {
  getPatientContacts,
  savePatientContact,
  deletePatientContact,
  setPrimaryPatientContact,
} from './patientContactsApi';

export const updatePreferences = (preferencesForm) => {
  const config = getRequestConfig(preferencesForm, UPDATE_PREFERENCES_ACTION, '/preferences/update');
  return new Promise((resolve, reject) => {
    request(config)
    .then((response) => {
      if (response.data.status === OK) {
        resolve(response.data.data);
      } else {
        reject(response.data.error);
      }
    })
    .catch((err) => {
      reject(err);
    });
  });
};

export const getPreferences = () => {
  const config = getRequestConfig({}, GET_PREFERENCES_ACTION, '/preferences/index');
  return new Promise((resolve, reject) => {
    request(config)
    .then((response) => {
      if (response.data.status === OK) {
        resolve(response.data.data);
      } else {
        reject(response.data.error);
      }
    })
    .catch((err) => {
      reject(err);
    });
  });
};

export const updateLicenseStatus = (licenseForm) => {
  const config = getRequestConfig(licenseForm, UPDATE_LICENSE_STATUS_ACTION, '/license/update-status');
  return new Promise((resolve, reject) => {
    request(config)
    .then((response) => {
      if (response.data.status === OK) {
        resolve(response.data.data);
      } else {
        reject(response.data.error);
      }
    })
    .catch((err) => {
      reject(err);
    });
  });
};

export const getLicenses = (licenseForm) => {
  const config = getRequestConfig(licenseForm, GET_LICENSES_ACTION, '/license/query');
  return new Promise((resolve, reject) => {
    request(config)
    .then((response) => {
      if (response.data.status === OK) {
        resolve(response.data.data);
      } else {
        reject(response.data.error);
      }
    })
    .catch((err) => {
      reject(err);
    });
  });
};

export const getContacts = (params = {}) => {
  return getPatientContacts(params);
};

export const createContact = (contactForm) => {
  return savePatientContact(contactForm);
};

export const updateContact = (contactForm) => {
  return savePatientContact(contactForm);
};

export const deleteContact = (contactForm) => {
  return deletePatientContact(contactForm);
};

export const renameContact = (contactForm) => {
  return savePatientContact(contactForm);
};

export const createDevice = (deviceForm) => {
  const config = getRequestConfig(deviceForm, CREATE_DEVICE, '/meter/create')
  return new Promise((resolve, reject) => {
    request(config)
    .then((response) => {
      if (response.data.status === OK) {
        resolve(response.data.data);
      } else {
        reject(response.data.error);
      }
    })
    .catch((err) => {
      reject(err);
    });
  })
}

export const setPrimaryContact = (contactForm) => {
  return setPrimaryPatientContact(contactForm);
};

export const updateProfile = (profileForm) => {
  const config = getRequestConfig(profileForm, UPDATE_PROFILE_ACTION, '/session/updateProfile');
  return new Promise((resolve, reject) => {
    request(config)
    .then((response) => {
      if (response.data.status === OK) {
        if(response.data.data.patient)
         {
           store.dispatch('login/inlineUpdateApplicationModel',response.data.data);
         }
        resolve(response.data.data);
      } else {
        reject(response.data.error);
      }
    })
    .catch((err) => {
      reject(err);
    });
  });
};

export const acquireByExternalId = (form) => {
  const config = getRequestConfig(form, null , '/session/acquireByExternalId');
  return new Promise((resolve, reject) => {
    request(config)
    .then((response) => {
      resolve(response.data.data);
    })
    .catch((err) => {
      reject(err);
    });
  });
};

export const summaryByExternalId = (form) => {
  const config = getRequestConfig(form, null , '/session/summaryByExternalId');
  return new Promise((resolve, reject) => {
    request(config)
    .then((response) => {
      resolve(response.data.data);
    })
    .catch((err) => {
      reject(err);
    });
  });
};

// --- creating a person -------------------------------------------------------

/**
 * Legacy account-type label -> the API's `role`.
 *
 * The labels are the option values the Add User form and My Account present;
 * snake-casing them lands exactly on `StoreUserRequest::CREATABLE_ROLES`, so the
 * table is a statement of that rather than a translation with opinions in it.
 * `Contact` is deliberately absent: the API refuses it here because a contact is
 * created by the contact-invitation flow, which carries the patient it attaches
 * to.
 */
const ROLE_BY_ACCOUNT_TYPE = {
  'Administrator': 'administrator',
  'Provider': 'provider',
  'Registered Nurse': 'registered_nurse',
  'Licensed Practical Nurse': 'licensed_practical_nurse',
  'Home Health Aid': 'home_health_aid',
  'Allied Professional': 'allied_professional',
  'Geriatric Care Manager': 'geriatric_care_manager',
  'Patient': 'patient',
};

/** `@/data/genders` is `{Male: 1, Female: 2}`; the API's enum is lower case. */
const GENDER_BY_LEGACY_ID = { 1: 'male', 2: 'female' };

/**
 * Add User's choices in the API's words: main's Email / SMS / Phone / All, plus
 * the port's postal Mail. 'All' is stored on the person's `all` notification
 * preference (pacmny-api CommunicationPreference, NT-12), which a create now
 * accepts as an edit does; before, it was dropped here because POST /users
 * refused it.
 */
const COMMUNICATION_PREFERENCES = ['email', 'sms', 'phone', 'mail', 'all'];

function accountRole(form) {
  const label = String(form.accountType || form.accountrole || form.role || '').trim();

  if (label === '') {
    // Five screens — PatientCards, CurrentNurse, PatientNurse, CurrentAid and
    // CurrentProviders — post this form from the same copy-pasted modal, whose
    // close handler is `closeNewProv` on all five and whose only credential
    // field is an NPI. It creates a provider; it just never said so, because
    // the legacy backend inferred the role from which table it wrote.
    return 'provider';
  }

  return ROLE_BY_ACCOUNT_TYPE[label] || label.toLowerCase().replace(/\s+/g, '_');
}

/**
 * The create-user body, built as an allowlist.
 *
 * An allowlist rather than a filtered copy of the form, because
 * `StoreUserRequest` *rejects* rather than ignores three groups of fields —
 * `accountnumber` / `organization_id` (tenancy comes from the token, and the
 * legacy forms choosing their own organization was the platform's central
 * defect), `password` / `logincode` (this service never writes a credential),
 * and chart content such as `allergies`. Every one of those is present on the
 * forms that call this, so passing the form through would 422 on all of them.
 */
function buildUserPayload(form) {
  const value = (v) => {
    const trimmed = typeof v === 'string' ? v.trim() : v;
    return trimmed === '' || trimmed === undefined ? null : trimmed;
  };
  const preference = String(form.pref || '').toLowerCase();

  return {
    role: accountRole(form),

    first_name: value(form.first),
    middle_name: value(form.middle),
    last_name: value(form.last),
    name_suffix: value(form.suffix),

    email: value(form.email),
    username: value(form.login),

    // `primaryPhone` is the Add User form's name, `cell` the provider modals'.
    phone_primary: value(form.primaryPhone) || value(form.cell),
    phone_secondary: value(form.work),

    dob: value(form.dob),
    gender: GENDER_BY_LEGACY_ID[Number(form.gender)] || null,

    address_line1: value(form.addr),
    city: value(form.city),
    state: value(form.state),
    postal_code: value(form.zip),

    // Each credential becomes a `user_licenses` row with its own status and
    // verification trail, never a string on the person.
    npi: value(form.npi),
    professional_license: value(form.licenseNum),
    license_state: value(form.licenseSt),
    certificate_number: value(form.certificateValue),

    specialty: value(form.specialty),
    provider_type: value(form.providerType),

    insurance_company: value(form.insuranceCompany),
    insurance_start_date: value(form.insuranceStart),
    insurance_end_date: value(form.insuranceEnd),
    insurance_per_incident_amount: value(form.perIncidentAmnt),
    insurance_annual_aggregate_amount: value(form.annualAggAmnt),

    // Add User's capitalised label, lower-cased ('All' -> 'all'). Anything the
    // API does not name is left unset rather than guessed.
    communication_preference: COMMUNICATION_PREFERENCES.includes(preference) ? preference : null,

    group_ids: Array.isArray(form.additionalTopLevelGroupIds) && form.additionalTopLevelGroupIds.length
      ? form.additionalTopLevelGroupIds
      : undefined,

    // What main sent in its second, keyon half (CreateSubAccount) travels in
    // this one create, so the person is made whole or not at all: the
    // licence and insurance files the form uploaded ahead of the save, by uid,
    // and the patients held for a new caregiver until they verify.
    license_file_uid: value(form.license_file_uid) || undefined,
    insurance_file_uid: value(form.insurance_file_uid) || undefined,
    pending_patient_ids: Array.isArray(form.pendingPatientIds) && form.pendingPatientIds.length
      ? form.pendingPatientIds
      : undefined,
  };
}

/**
 * Create a person in this organization.
 *
 * `POST /users`, replacing `/sessionless/register`. Two things change and both
 * are deliberate:
 *
 *  - **No password crosses this boundary.** The legacy form posted one and every
 *    caller filled it with a placeholder (`"PACMNY_Reg1!"` in `AddUserForm`) so
 *    that a credential nobody chose existed on a real account until the invitee
 *    replaced it. The new user is created `invited`, and pacmny-auth mails them
 *    an invitation to choose their own.
 *  - **The organization is not in the body.** It comes from the access token.
 *
 * Resolves the shape the call sites already read — `data.externid` and
 * `data.userId` are both the new user's id, and `username` is what the server
 * settled on.
 */
export const register = (registrationForm) => {
  return createUser(buildUserPayload(registrationForm)).then((user) => ({
    externid: user.id,
    userId: user.id,
    username: user.username,
    user,
  }));
};

// --- is this identifier already taken? ---------------------------------------
//
// Four legacy operations across two databases collapse onto
// `GET /users/availability` (signed-in only since Trello fbd6q9UZ, and
// hard-throttled because it is an existence oracle). They used to disagree:
// keyon and PostgreSQL each held half the identity data and each probe consulted
// only its own half. One database now, one answer.
//
// The endpoint answers `{available, checked}` and these four answer "taken", so
// each negates it — the same translation the bridge already makes for the keyon
// spelling of the same probe (`Account.IsNpiExist`).

const isTaken = (params) => checkAvailability(params).then((answer) => !(answer && answer.available));

export const isUserExist = (username) => isTaken({ username });

export const isEmailExist = (email) => isTaken({ email });

export const isNpiExist = (npi) => isTaken({ npi });

/**
 * Is this account *role* already taken? — which is not a question anything can
 * answer.
 *
 * `MyAccount.validatePerm` passes `form.permission`, which line 1156 of that
 * component assigns straight to `nwform.accountrole`: it is "Administrator",
 * "Provider" and so on, not an identifier. There is no
 * `IsPermExistAction` in pacmny-be either (`controllers/sessionless/` has
 * twelve actions and none of them is this one), so the legacy call 404'd and the
 * caller alerted — this preserves that outcome and says why, instead of
 * resolving `false` and letting a duplicate check silently pass everything.
 *
 * The availability endpoint checks npi, email, username, licence and
 * certificate. If the intent was one of those, the form has to say which.
 */
export const isPermExist = () => Promise.reject(new Error(
  'This duplicate check has no endpoint: it was passing an account role, not an identifier, '
  + 'and pacmny-be never implemented it either. Nothing was checked.'
));

// --- changing how someone signs in -------------------------------------------
//
// `PATCH /users/{user}/credentials`, replacing `/sessionless/updateUsername` and
// `/sessionless/updateEmail`. Its own scope (`users:credentials`) and its own
// audit type, because seeing that a colleague exists and changing how they sign
// in are different rights — the legacy pair required neither.
//
// Both take the id under the legacy key `externid`, which is what the call sites
// hold; it is the user id now.

/** Change an invited or existing user's username. */
export const updateUsername = (form) => updateUserCredentials(form.externid, { username: form.username });

/** Change an invited or existing user's e-mail. */
export const updateEmail = (form) => updateUserCredentials(form.externid, { email: form.email });

export const changePassword = (form) => {
  const config = getRequestConfig(form, CHANGE_PASSWORD_ACTION, '/session/changePassword');
  return new Promise((resolve, reject) => {
    request(config)
      .then((response) => {
        if (response.data.status === OK) {
          resolve(response.data);
        } else {
          reject(response.data.message || 'Unable to change password.');
        }
      })
      .catch((err) => {
        reject(err);
      });
  });
};

// export const createContact = (contactForm) => {
//   const config = getRequestConfig(contactForm, CREATE_CONTACT_ACTION, '/contacts/add');
//   return new Promise((resolve, reject) => {
//     request(config)
//     .then((response) => {
//       if (response.data.status === OK) {
//         resolve(response.data.data);
//       } else {
//         reject(response.data.error);
//       }
//     })
//     .catch((err) => {
//       reject(err);
//     });
//   });
// };

export const updateNotes = (form) => {
  const config = getRequestConfig(form, UPDATE_NOTES, '/notes/update')
  return new Promise((resolve, reject) => {
    request(config)
    .then((response) => {
      if (response.data.status === OK) {
        resolve(response.data.data)
      }
      else {
        reject(response.data.error)
      }
    })
    .catch((err) => {
      reject(err)
    })
  })
}

export const deleteNotes = (form) => {
  const config = getRequestConfig(form, DELETE_NOTES, '/notes/delete')
  return new Promise((resolve, reject) => {
    request(config)
    .then((response) => {
      if (response.data.status === OK) {
        resolve(response.data.data)
      }
      else {
        reject(response.data.error)
      }
    })
    .catch((err) => {
      reject(err)
    })
  })
}

export const getNotes = (form) => {
  const config = getRequestConfig(form, GET_NOTES, '/notes/index')
  return new Promise((resolve, reject) => {
    request(config)
    .then((response) => {
      if (response.data.status === OK) {
        resolve(response.data.data)
      }
      else {
        reject(response.data.error)
      }
    })
    .catch((err) => {
      reject(err)
    })
  })
}